import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Tooltip,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";

import { RootState } from "../../store/Store";
import {
  fetchData,
  postData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import PageTitle from "../../components/Page Component/PageTitle";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  complianceDashboardUrl,
  complianceTeamStatusUrl,
  sendComplianceNotificationsUrl,
  processComplianceAssignmentsUrl,
} from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import ConfirmDialog from "../../components/Dialogs/ConfirmDialog";
import AttendanceDateFilters from "../../components/Attendance/AttendanceDateFilters";
import { titleCase } from "./utils";
import { getDataGridPaginationProps, PAGINATION_CONFIG } from "utils/paginationConfig";

interface DashboardStats {
  total_assignments: number;
  completed: number;
  pending: number;
  in_progress: number;
  overdue: number;
  compliance_rate: number;
  upcoming_due: number;
  forms_filled_count: number;
  forms_filled_percentage: number;
}

interface TeamComplianceData {
  team_id: string;
  team_name: string;
  manager_name: string;
  total_assignments: number;
  completed: number;
  pending: number;
  in_progress: number;
  overdue: number;
  compliance_rate: number;
}

const ComplianceDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [teamData, setTeamData] = useState<TeamComplianceData[]>([]);
  const [overdueAssignments, setOverdueAssignments] = useState<any[]>([]);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [startMonth, setStartMonth] = useState<Dayjs | null>(null);
  const [endMonth, setEndMonth] = useState<Dayjs | null>(null);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  const buildDateParams = useCallback(
    (prefix = "&") => {
      if (!startMonth && !endMonth) return "";
      const parts: string[] = [];
      if (startMonth) parts.push(`start_month=${startMonth.format("YYYY-MM")}`);
      if (endMonth) parts.push(`end_month=${endMonth.format("YYYY-MM")}`);
      return prefix + parts.join("&");
    },
    [startMonth, endMonth]
  );

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth, endMonth]);

  const fetchDashboardData = async () => {
    try {
      dispatch(setIsLoading(true));
      const dateParams = buildDateParams();

      // Fetch dashboard statistics
      const dashboardStats = await dispatch2(
        fetchData(`${complianceDashboardUrl}?role=${selectedRole}${dateParams}`)
      );
      setStats(dashboardStats as DashboardStats);

      // Fetch team compliance data if admin/HR
      if (selectedRole === "Admin" || selectedRole === "HR") {
        const teamsData = await dispatch2(
          fetchData(`${complianceTeamStatusUrl}?role=${selectedRole}${dateParams}`)
        );
        setTeamData(teamsData as TeamComplianceData[]);
      }

      // Fetch overdue assignments scoped to the selected date range
      const overdueParams = startMonth || endMonth
        ? `${dateParams}`
        : (() => {
            const now = new Date();
            const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            return `&month_year=${currentMonthYear}`;
          })();
      const assignments = await dispatch2(
        fetchData(
          `${processComplianceAssignmentsUrl}?role=${selectedRole}&status=overdue${overdueParams}`
        )
      );
      setOverdueAssignments(assignments);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      showToast("Error fetching dashboard data", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleSendNotifications = async () => {
    setNotificationDialogOpen(true);
  };

  const executeNotificationSend = async () => {
    try {
      dispatch(setIsLoading(true));
      const dateParams = buildDateParams();

      const response = (await dispatch2(
        postData(`${sendComplianceNotificationsUrl}?role=${selectedRole}${dateParams}`, {})
      )) as any;

      if (response && response.notifications_sent > 0) {
        showToast(
          `Sent ${response.notifications_sent} notifications successfully`,
          "success",
          true
        );
      } else {
        showToast(
          response.message || "No notifications were sent",
          "info",
          true
        );
      }
    } catch (error) {
      console.error("Error sending notifications:", error);
      showToast("Error sending notifications", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleNavigateToAssignments = () => {
    navigate("/process-compliance-assignments");
  };

  // Prepare chart data for team compliance rates using recharts
  const chartData = teamData.map((team) => ({
    name: team.team_name,
    rate: team.compliance_rate,
    color:
      team.compliance_rate >= 90
        ? "#4caf50"
        : team.compliance_rate >= 70
        ? "#ff9800"
        : "#f44336",
  }));

  // Grid columns for overdue assignments
  const overdueColumns: GridColDef[] = [
    { field: "project_name", headerName: "Project", flex: 1, minWidth: 180, renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Project</strong>), renderCell: (params) => params.value || "—" },
    { field: "template_name", headerName: "Template", flex: 1, minWidth: 180, renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Template</strong>) },
    { field: "assigned_to_name", headerName: "Assigned To", width: 180, renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Assigned To</strong>) },
    {
      field: "due_date",
      headerName: "Due Date",
      width: 120,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString("en-GB");
      }, renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Due Date </strong>)
    },
    { field: "month_year_str", headerName: "Month/Year", width: 120, renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Month/Year</strong>) },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            navigate(
              `/process-compliance-assignments?id=${params.row.assignment_id}`
            )
          }
        >
          View
        </Button>
      ),renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Actions </strong>)
    },
  ];

  // Grid columns for team compliance data
  const teamColumns: GridColDef[] = [
    { field: "team_name", headerName: "Team", flex: 1, minWidth: 180 },
    { field: "manager_name", headerName: "Manager", width: 180 },
    { field: "total_assignments", headerName: "Total", width: 80 },
    { field: "completed", headerName: "Completed", width: 100 },
    { field: "pending", headerName: "Pending", width: 100 },
    { field: "overdue", headerName: "Overdue", width: 100 },
    {
      field: "compliance_rate",
      headerName: "Compliance Rate",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ width: "100%", mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={params.value}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    params.value >= 90
                      ? "success.main"
                      : params.value >= 70
                      ? "warning.main"
                      : "error.main",
                  borderRadius: 5,
                },
              }}
            />
          </Box>
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="black">
              {`${Math.round(params.value)}%`}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  // Custom tooltip for the bar chart
  const CustomBarTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1 }}>
          <Typography variant="body2">{`${payload[0].name}: ${payload[0].value}%`}</Typography>
        </Paper>
      );
    }
    return null;
  };

  const renderNotificationDialog = () => (
    <ConfirmDialog
      open={notificationDialogOpen}
      title="Send Compliance Reminders"
      message="This will send email reminders to all users with pending or upcoming compliance forms. Continue?"
      severity="info"
      confirmButtonText="Send Reminders"
      cancelButtonText="Cancel"
      onConfirm={() => {
        setNotificationDialogOpen(false);
        executeNotificationSend();
      }}
      onCancel={() => {
        setNotificationDialogOpen(false);
      }}
    />
  );

  // Compute current month label once for display
  const currentMonthLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }, []);

  // Dynamic subtitle reflecting the active date filter
  const filterLabel = useMemo(() => {
    if (startMonth && endMonth)
      return `Stats shown for: ${startMonth.format("MMM YYYY")} – ${endMonth.format("MMM YYYY")}`;
    if (startMonth)
      return `Stats shown from: ${startMonth.format("MMM YYYY")}`;
    if (endMonth)
      return `Stats shown up to: ${endMonth.format("MMM YYYY")}`;
    return `Stats shown for current month: ${currentMonthLabel}`;
  }, [startMonth, endMonth, currentMonthLabel]);

  // Label for the overdue section header
  const overdueSectionLabel = useMemo(() => {
    if (startMonth && endMonth)
      return `${startMonth.format("MMM YYYY")} – ${endMonth.format("MMM YYYY")}`;
    if (startMonth) return `from ${startMonth.format("MMM YYYY")}`;
    if (endMonth) return `up to ${endMonth.format("MMM YYYY")}`;
    return currentMonthLabel;
  }, [startMonth, endMonth, currentMonthLabel]);

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>Process Compliance Dashboard</PageTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {filterLabel}
            </Typography>
            <AttendanceDateFilters
              fromDate={startMonth}
              toDate={endMonth}
              onFromDateChange={(d) => setStartMonth(d)}
              onToDateChange={(d) => setEndMonth(d)}
              onClear={() => {
                setStartMonth(null);
                setEndMonth(null);
              }}
              fromLabel="From Month"
              toLabel="To Month"
            />
          </Box>
        </Box>

        {/* Stats overview cards */}
        {stats && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    color="black"
                    gutterBottom
                  >
                    Total Assignments
                  </Typography>
                  <Typography variant="h4">
                    {stats.total_assignments}
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" color="success.main" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.completed}
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (stats.completed / stats.total_assignments) * 100 || 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#4caf50",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ color: "#f9a825" }} gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" sx={{ color: "#f9a825" }}>
                    {stats.pending}
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (stats.pending / stats.total_assignments) * 100 || 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(249, 168, 37, 0.2)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#f9a825",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" color="error.main" gutterBottom>
                    Overdue
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.overdue}
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (stats.overdue / stats.total_assignments) * 100 || 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(244, 67, 54, 0.2)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#f44336",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Compliance Status + Forms Filled Progress Cards */}
        {stats && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    color="black"
                    gutterBottom
                  >
                    Forms Filled Progress
                  </Typography>
                  <Box display="flex" alignItems="flex-end">
                    <Typography variant="h5" mr={1}>
                      {stats.forms_filled_count}/{stats.total_assignments}
                    </Typography>
                    <Typography variant="body2" color="black" mb={1}>
                      forms completed ({stats.forms_filled_percentage}%)
                    </Typography>
                  </Box>
                  <Box mt={1} sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: "100%", mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={stats.forms_filled_percentage}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: "#e0e0e0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor:
                              stats.forms_filled_percentage >= 90
                                ? "success.main"
                                : stats.forms_filled_percentage >= 70
                                ? "warning.main"
                                : "error.main",
                            borderRadius: 6,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    color="black"
                    gutterBottom
                  >
                    Compliance Status
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Box textAlign="center">
                      <Typography variant="h5" color="success.main">
                        {stats.completed}
                      </Typography>
                      <Typography variant="body2">Completed</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box textAlign="center">
                      <Typography variant="h5" color="info.main">
                        {stats.in_progress}
                      </Typography>
                      <Typography variant="body2">In Progress</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box textAlign="center">
                      <Typography variant="h5" color="warning.main">
                        {stats.pending}
                      </Typography>
                      <Typography variant="body2">Pending</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box textAlign="center">
                      <Typography variant="h5" color="error.main">
                        {stats.overdue}
                      </Typography>
                      <Typography variant="body2">Overdue</Typography>
                    </Box>
                  </Box>

                  <Box
                    mt={3}
                    mb={1}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Button
                      variant="outlined"
                      onClick={handleNavigateToAssignments}
                    >
                      View All Assignments
                    </Button>

                    {(selectedRole === "Admin" || selectedRole === "HR") && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setNotificationDialogOpen(true)}
                      >
                        Send Reminders
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Overdue Assignments Section */}
        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" mb={2} fontWeight="medium">
            Overdue Assignments — {overdueSectionLabel}
          </Typography>
          <DataGrid
            rows={overdueAssignments}
            columns={overdueColumns}
            getRowId={(row) => row.assignment_id}
            autoHeight
            {...getDataGridPaginationProps()}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
            }}
            sx={{ minHeight: 300 }}
          />
        </Paper>

        {/* Team Compliance Section - Only for Admin/HR */}
        {(selectedRole === "Admin" || selectedRole === "HR") &&
          teamData.length > 0 && (
            <>
              <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6" mb={2} fontWeight="medium">
                  Team Compliance Overview
                </Typography>
                <DataGrid
                  rows={teamData}
                  columns={teamColumns}
                  getRowId={(row) => row.team_id}
                  autoHeight
                  initialState={{
                    ...getDataGridPaginationProps().initialState,
                    sorting: {
                      sortModel: [{ field: "compliance_rate", sort: "asc" }],
                    },
                  }}
                  pageSizeOptions={getDataGridPaginationProps().pageSizeOptions}
                  disableRowSelectionOnClick
                  slots={{
                    noRowsOverlay: CustomNoRowsOverlay,
                    noResultsOverlay: CustomNoRowsOverlay,
                  }}
                  sx={{ minHeight: 300 }}
                />
              </Paper>

              {/* Team Compliance Chart */}
              <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6" mb={2} fontWeight="medium">
                  Team Compliance Chart
                </Typography>
                <Box sx={{ height: 400, width: "100%" }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis
                        domain={[0, 100]}
                        label={{
                          value: "Compliance Rate (%)",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <RechartsTooltip content={<CustomBarTooltip />}
                        wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }} />
                      <Legend />
                      <Bar
                        dataKey="rate"
                        name="Compliance Rate"
                        barSize={60}
                        radius={[10, 10, 0, 0]}
                        fill="#82ca9d"
                        fillOpacity={0.8}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </>
          )}
      </Container>

      {renderNotificationDialog()}
    </>
  );
};

export default ComplianceDashboard;
